import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Check, ChevronDown, Search, X } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface DropdownOption {
  value: string
  label: string
  searchValue?: string
}

interface Props {
  value: string
  options: DropdownOption[]
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  buttonClassName?: string
  panelClassName?: string
  disabled?: boolean
  /** Open the panel upward instead of downward (e.g. when near the bottom of the page). */
  dropUp?: boolean
  searchable?: boolean
  /** Server-side search: the parent supplies already-filtered options; internal filtering is skipped. */
  onSearchChange?: (query: string) => void
  searchPlaceholder?: string
  emptyLabel?: string
  clearLabel?: string
  onClear?: () => void
  startIcon?: ReactNode
  renderValue?: (selected: DropdownOption | null) => ReactNode
  renderOption?: (option: DropdownOption, isSelected: boolean) => ReactNode
}

export function DropdownSelect({
  value,
  options,
  onChange,
  placeholder = 'Select',
  className,
  buttonClassName,
  panelClassName,
  disabled,
  dropUp = false,
  searchable = false,
  onSearchChange,
  searchPlaceholder = 'Search',
  emptyLabel = 'No options found.',
  clearLabel = 'Clear',
  onClear,
  startIcon,
  renderValue,
  renderOption,
}: Props) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  const selected = options.find(option => option.value === value) ?? null

  const filteredOptions = useMemo(() => {
    if (onSearchChange) return options  // parent owns filtering
    const normalized = search.trim().toLowerCase()
    if (!normalized) return options

    return options.filter(option => {
      const haystack = `${option.label} ${option.searchValue ?? ''}`.toLowerCase()
      return haystack.includes(normalized)
    })
  }, [options, search, onSearchChange])

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    if (!open) {
      setSearch('')
    }
  }, [open])

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen(current => !current)}
        className={cn('input flex items-center justify-between gap-2 w-full text-left', buttonClassName)}
        aria-expanded={open}
        disabled={disabled}
      >
        <span className="flex items-center gap-2 min-w-0">
          {startIcon && <span className="flex-shrink-0 text-gray-500">{startIcon}</span>}
          {renderValue ? (
            renderValue(selected)
          ) : (
            <span className={cn('truncate', selected ? 'text-gray-900' : 'text-gray-400')}>
              {selected?.label ?? placeholder}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-150',
            open ? 'rotate-180' : '',
          )}
        />
      </button>

      <div
        className={cn(
          'absolute z-50 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden transition-all duration-150 ease-out',
          dropUp ? 'bottom-full mb-1 origin-bottom' : 'top-full mt-1 origin-top',
          open
            ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
            : cn('opacity-0 scale-95 pointer-events-none', dropUp ? 'translate-y-1' : '-translate-y-1'),
          panelClassName,
        )}
      >
        {searchable && (
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                value={search}
                onChange={event => { setSearch(event.target.value); onSearchChange?.(event.target.value) }}
                placeholder={searchPlaceholder}
                className="input py-1.5 pl-8 pr-8"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        <div className="max-h-64 overflow-y-auto py-1">
          {filteredOptions.length === 0 && (
            <p className="px-3 py-2 text-sm text-gray-500">{emptyLabel}</p>
          )}

          {filteredOptions.map(option => {
            const isSelected = option.value === value

            return (
              <button
                key={option.value}
                type="button"
                title={option.label}
                onClick={() => {
                  onChange(option.value)
                  setOpen(false)
                }}
                className={cn(
                  'w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors',
                  isSelected ? 'bg-gray-50 font-medium text-gray-900' : 'text-gray-700',
                )}
              >
                {renderOption ? (
                  renderOption(option, isSelected)
                ) : (
                  <span className="truncate">{option.label}</span>
                )}
                {!renderOption && isSelected && <Check className="w-4 h-4 text-primary-600 flex-shrink-0" />}
              </button>
            )
          })}
        </div>

        {onClear && value && (
          <button
            type="button"
            onClick={() => {
              onClear()
              setOpen(false)
            }}
            className="w-full flex items-center gap-2 px-3 py-2 border-t border-gray-100 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <X className="w-4 h-4" />
            {clearLabel}
          </button>
        )}
      </div>
    </div>
  )
}
